import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { messagesAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';

const ChatWidget = ({ sellerId, sellerName, itemTitle }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sendMessage, onNewMessage, onMessageSent, emitTyping, emitStopTyping, onTyping, onUserOnline, onUserOffline } = useSocket();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [convId, setConvId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sellerOnline, setSellerOnline] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);
  const typingIndicatorTimeout = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Start or load conversation when widget opens
  useEffect(() => {
    if (!open || !user || !sellerId || String(sellerId) === String(user.id)) return;
    setLoading(true);
    messagesAPI.startConversation({ user_id: sellerId })
      .then((res) => {
        const conv = res.data.conversation;
        setConvId(conv.id);
        return messagesAPI.getMessages(conv.id);
      })
      .then((res) => setMessages(res.data.messages || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, user, sellerId]);

  // Listen for incoming messages
  useEffect(() => {
    if (!convId) return;
    const unsub = onNewMessage((msg) => {
      if (String(msg.conversation_id) === String(convId)) {
        setMessages((prev) => [...prev, { sender_id: msg.sender_id, message: msg.message, created_at: msg.created_at }]);
      }
    });
    return unsub;
  }, [convId, onNewMessage]);

  // Listen for sent confirmation
  useEffect(() => {
    if (!convId) return;
    const unsub = onMessageSent((msg) => {
      if (String(msg.conversation_id) === String(convId)) {
        // Already added optimistically
      }
    });
    return unsub;
  }, [convId, onMessageSent]);

  // Listen for typing indicator
  useEffect(() => {
    if (!convId) return;
    const unsub = onTyping((data) => {
      if (String(data.conversation_id) === String(convId) && String(data.userId) === String(sellerId)) {
        setIsTyping(true);
        clearTimeout(typingIndicatorTimeout.current);
        typingIndicatorTimeout.current = setTimeout(() => setIsTyping(false), 3000);
      }
    });
    return unsub;
  }, [convId, sellerId, onTyping]);

  // Online/offline status
  useEffect(() => {
    if (!sellerId) return;
    const unsubOn = onUserOnline((data) => { if (String(data.userId) === String(sellerId)) setSellerOnline(true); });
    const unsubOff = onUserOffline((data) => { if (String(data.userId) === String(sellerId)) setSellerOnline(false); });
    return () => { unsubOn(); unsubOff(); };
  }, [sellerId, onUserOnline, onUserOffline]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !sellerId) return;
    setMessages((prev) => [...prev, { sender_id: user.id, message: newMsg.trim(), created_at: new Date().toISOString() }]);
    sendMessage(sellerId, newMsg.trim());
    setNewMsg('');
    if (convId) emitStopTyping(sellerId, convId);
    clearTimeout(typingTimeout.current);
  };

  const handleInputChange = (e) => {
    setNewMsg(e.target.value);
    if (convId && sellerId) {
      emitTyping(sellerId, convId);
      clearTimeout(typingTimeout.current);
      typingTimeout.current = setTimeout(() => emitStopTyping(sellerId, convId), 2000);
    }
  };

  const sendQuickMessage = () => {
    const msg = `Hi, I'm interested in "${itemTitle}". Is it still available?`;
    setMessages((prev) => [...prev, { sender_id: user.id, message: msg, created_at: new Date().toISOString() }]);
    sendMessage(sellerId, msg);
  };

  const handleOpen = () => {
    if (!user) return navigate('/login');
    setOpen(true);
  };

  // Don't show button for own items
  const isOwn = user && String(sellerId) === String(user.id);
  if (isOwn) return null;

  return (
    <>
      {/* Inline Chat Button - sits alongside WhatsApp/Call */}
      <button
        onClick={handleOpen}
        className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 transition text-sm mt-3"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        Chat with Seller
      </button>

      {/* Chat Panel Overlay */}
      {open && (
        <div className="fixed bottom-4 right-4 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden" style={{ height: '480px' }}>
          {/* Header */}
          <div className="bg-primary-600 text-white px-4 py-3 flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                {sellerName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-primary-600 ${sellerOnline ? 'bg-green-400' : 'bg-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{sellerName}</div>
              <div className="text-primary-200 text-xs truncate">
                {isTyping ? 'typing…' : sellerOnline ? 'Online' : itemTitle}
              </div>
            </div>
            <Link to="/messages" className="text-white/70 hover:text-white p-1" title="Full messages">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            </Link>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="text-4xl mb-3">💬</div>
                <p className="text-gray-500 text-sm mb-3">Start a conversation about</p>
                <p className="font-medium text-gray-700 text-sm mb-4">"{itemTitle}"</p>
                <button onClick={sendQuickMessage}
                  className="bg-primary-50 text-primary-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-100 transition">
                  Send Quick Message
                </button>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => {
                  const isMine = String(msg.sender_id) === String(user.id);
                  return (
                    <div key={i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                        isMine ? 'bg-primary-600 text-white rounded-br-md' : 'bg-white text-gray-800 rounded-bl-md shadow-sm border'
                      }`}>
                        <p className="break-words">{msg.message || msg.content}</p>
                        <p className={`text-[10px] mt-0.5 ${isMine ? 'text-primary-200' : 'text-gray-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white text-gray-500 rounded-2xl rounded-bl-md shadow-sm border px-4 py-2 text-sm flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="p-3 border-t bg-white flex gap-2 items-center">
            <input
              type="text"
              value={newMsg}
              onChange={handleInputChange}
              placeholder="Type a message..."
              className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={!newMsg.trim()}
              className="bg-primary-600 text-white w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary-700 disabled:opacity-40 transition flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
